import { memo, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ThumbsUp, ThumbsDown, Ghost, Sparkles, Eye,
  AlertTriangle, HelpCircle, Radio, Moon,
  Smile, ChevronUp, ChevronDown, Crosshair,
} from "lucide-react";

/**
 * Single source of truth for the emote palette.
 * Order here = order in the bar; `id` must match backend ALLOWED_EMOTE_IDS.
 */
export const EMOTES = [
  { id: "thumbs_up",   Icon: ThumbsUp,      label: "thumbs up"      },
  { id: "thumbs_down", Icon: ThumbsDown,    label: "thumbs down"    },
  { id: "excited",     Icon: Sparkles,      label: "excited"        },
  { id: "scared",      Icon: Ghost,         label: "scared"         },
  { id: "watching",    Icon: Eye,           label: "watching"       },
  { id: "alert",       Icon: AlertTriangle, label: "alert"          },
  { id: "question",    Icon: HelpCircle,    label: "question"       },
  { id: "signal",      Icon: Radio,         label: "broadcasting"   },
  { id: "moon",        Icon: Moon,          label: "after hours"    },
];

const EMOTE_INDEX = Object.fromEntries(EMOTES.map((e) => [e.id, e]));

export const getEmoteIcon = (id) => EMOTE_INDEX[id] || null;

/**
 * Cooldown ring overlay — a thin circular SVG that drains over `seconds`.
 */
const CooldownRing = ({ remaining, total }) => {
  const r = 18;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, remaining / total));
  return (
    <svg className="emote-cooldown-ring" viewBox="0 0 40 40">
      <circle
        cx="20" cy="20" r={r}
        fill="none"
        stroke="rgba(255,255,255,0.85)"
        strokeWidth="2"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        transform="rotate(-90 20 20)"
        strokeLinecap="round"
      />
    </svg>
  );
};

const EmoteBarBase = ({ watcherId, cooldownSeconds = 8, onSent, activeCount = 0, onMark, markState = { canMark: false, label: "" } }) => {
  const [open, setOpen] = useState(false);
  const [slide, setSlide] = useState("emote"); // "emote" | "mark"
  // ms timestamp the cooldown expires (0 = no cooldown)
  const [cooldownEnd, setCooldownEnd] = useState(0);
  const [tick, setTick] = useState(0);

  // Drive a clock for the emote cooldown ring repaint (the mark countdown is
  // driven by markState from the parent's 1 Hz clock).
  useEffect(() => {
    if (!cooldownEnd) return undefined;
    const i = setInterval(() => setTick((t) => t + 1), 250);
    return () => clearInterval(i);
  }, [cooldownEnd]);

  const now = Date.now();
  const remainingMs = Math.max(0, cooldownEnd - now);
  const onCooldown = remainingMs > 0;
  // Silence "tick is unused" — it's only here to force re-renders
  void tick;

  const send = async (emoteId, label) => {
    if (onCooldown) return;
    // Optimistically lock UI; reconcile with server response
    setCooldownEnd(Date.now() + cooldownSeconds * 1000);
    try {
      const res = await onSent(emoteId);
      if (res && res.success === false && res.reason === "cooldown") {
        // Server says we're still cooling down — honour it.
        setCooldownEnd(Date.now() + Math.ceil((res.retry_after || cooldownSeconds) * 1000));
        toast.error(`Cooling down · ${Math.ceil(res.retry_after || cooldownSeconds)}s`);
      } else if (res && res.success) {
        toast.success(`You sent ${label}`, { duration: 1500 });
      } else if (res && res.reason === "invalid_or_no_location") {
        toast.error("Enable location to send emotes");
        setCooldownEnd(0);
      }
    } catch {
      setCooldownEnd(0);
    }
  };

  return (
    <div className={`emote-bar ${open ? "open" : ""}`} data-testid="emote-bar">
      <button
        className="emote-bar-toggle"
        onClick={() => setOpen(!open)}
        data-testid="emote-bar-toggle"
        title={open ? "Hide emotes" : "Send an emote"}
      >
        <Smile size={14} />
        <span className="emote-bar-toggle-label">EMOTE</span>
        {activeCount > 0 && (
          <span className="emote-bar-toggle-count" data-testid="emote-bar-count">
            {activeCount}
          </span>
        )}
        {open ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
      </button>

      <div className="emote-bar-slides" aria-hidden={!open} inert={!open}>
        <button
          className={`emote-slide-tab ${slide === "emote" ? "active" : ""}`}
          onClick={() => setSlide("emote")}
          data-testid="slide-emote"
        >
          <Smile size={12} /> EMOTES
        </button>
        <button
          className={`emote-slide-tab ${slide === "mark" ? "active" : ""}`}
          onClick={() => setSlide("mark")}
          data-testid="slide-mark"
        >
          <Crosshair size={12} /> MARK
        </button>
      </div>

      {slide === "emote" ? (
        <div className="emote-bar-tray" aria-hidden={!open} inert={!open}>
          {EMOTES.map(({ id, Icon, label }) => (
            <button
              key={id}
              className={`emote-btn ${onCooldown ? "disabled" : ""}`}
              data-testid={`emote-btn-${id}`}
              onClick={() => send(id, label)}
              disabled={onCooldown}
              title={label}
              aria-label={`Send ${label}`}
            >
              <Icon size={18} strokeWidth={1.6} />
              {onCooldown && (
                <CooldownRing remaining={remainingMs / 1000} total={cooldownSeconds} />
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="mark-slide" aria-hidden={!open} inert={!open}>
          <p className="mark-slide-hint">
            Drops a marker at the crosshair (map centre). Everyone sees it — and it
            might attract attention. Marking opens for one minute every ten,
            and only after you've been watching ten minutes.
          </p>
          <button
            className={`mark-slide-btn ${markState.canMark ? "" : "disabled"}`}
            onClick={() => markState.canMark && onMark && onMark()}
            disabled={!markState.canMark}
            data-testid="mark-place-btn"
          >
            <Crosshair size={16} />
            {markState.label || "MARK THIS SPOT"}
          </button>
        </div>
      )}
    </div>
  );
};

export const EmoteBar = memo(EmoteBarBase);
